import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Search } from 'components/SearchBar/components';
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Button,
  CardHeader,
  Divider,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid
} from '@material-ui/core';
import { CELL_MODELS } from 'common/constants';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 850,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  progress: {
    marginBottom: theme.spacing(2)
  },
  fileName: {
    marginBottom: theme.spacing(2)
  },
  image: {
    width: '100%'
  },
  details: {
    padding: theme.spacing(2)
  },
  comment: {
    flexGrow: 1,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    position: 'relative',
    marginTop: theme.spacing(1)
  },
  search: {
    minWidth: 480,
    maxWidth: 480,
    flexBasis: 480
  },
  tableContainer: {
    minHeight: 450,
    maxHeight: 450,
    overflowY: 'auto'
  },
  searchContainer: {
    padding: theme.spacing(2)
  },
  selfSearch: {
    marginTop: 3
  },
  buttonCell: {
    paddingTop: 0,
    paddingBottom: 0
  },
  buttonExtraSmall: {
    padding: '1px 8px',
    fontSize: '0.8125rem'
  },
  actionsColumn: {
    width: 100
  }
}));

const CellModelSelect = props => {
  const {
    open,
    onClose,
    onSelectCellModel,
    selectedCellModelCodes,
    className,
    ...rest
  } = props;

  const classes = useStyles();

  const loading = false;
  const [searchName, setSearchName] = useState('');
  const cellModels = Object.keys(CELL_MODELS).map(key => ({
    code: key,
    name: CELL_MODELS[key]
  }));

  const findCellModelsByName = searchName => {
    return cellModels.filter(model => {
      return (
        searchName === '' ||
        model.name.toUpperCase().indexOf(searchName.toUpperCase()) !== -1
      );
    });
  };

  const searchedCellModels = findCellModelsByName(searchName);

  const handleClose = () => {
    onClose();
  };

  const handleSearchFieldChange = value => {
    setSearchName(value);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal onClose={handleClose} open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <CardHeader title="Select Cell Model" />
        <Divider />

        <CardContent className={classes.content}>
          <div className={classes.searchContainer}>
            <Grid container spacing={3}>
              <Grid item>
                <Search
                  placeholder="Name"
                  value={searchName}
                  onSearchValueChange={handleSearchFieldChange}
                  className={classes.search}
                  hideSearchButton={true}
                />
              </Grid>
            </Grid>
          </div>
          <Divider />
          <div className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.actionsColumn}>
                    Actions
                  </TableCell>
                  <TableCell>Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchedCellModels.map(cellModel => (
                  <TableRow key={cellModel.code} hover>
                    <TableCell className={classes.buttonCell}>
                      <Button
                        disabled={
                          selectedCellModelCodes &&
                          selectedCellModelCodes.indexOf(cellModel.code) > -1
                        }
                        className={classes.buttonExtraSmall}
                        color="primary"
                        size="small"
                        variant="outlined"
                        onClick={() => onSelectCellModel(cellModel)}>
                        Select
                      </Button>
                    </TableCell>
                    <TableCell>{cellModel.name}</TableCell>
                  </TableRow>
                ))}
                {!loading && searchedCellModels.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2}>No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <CardActions className={classes.actions}>
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

CellModelSelect.displayName = 'CellModelSelect';

CellModelSelect.propTypes = {
  className: PropTypes.string
};

export default CellModelSelect;
